import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from 'react-bootstrap';
import HTMLPreview from '../components/Layout/HTMLPreview';
import { fetchLatestConference, fetchLatestNews } from '../services/api';

const Home = () => {
    const [latestConference, setLatestConference] = useState(null);
    const [latestNews, setLatestNews] = useState([]);
    const [loading, setLoading] = useState(true);

    // Fetch latest conference and news on component mount
    useEffect(() => {
        fetchLatestData();
    }, []);

    const fetchLatestData = async () => {
        try {
            // Fetch latest conference and news in parallel
            const [conference, news] = await Promise.all([
                fetchLatestConference(),
                fetchLatestNews(4)
            ]);
            
            setLatestConference(conference[0]);
            setLatestNews(news);            
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Replace file references in description with actual URLs
    const processDescription = (desc, files) => {
        if (!desc || !files) return desc;
        
        let processedDesc = desc;
        
        files.forEach(file => {
            // Replace relative URLs with full URLs
            const filePattern = new RegExp(`/news/files/${file.filename}`, 'g');
            processedDesc = processedDesc.replace(
                filePattern,
                `${process.env.REACT_APP_API_URL || '/api'}/news/files/${file.filename}`
            );
        });
        
        return processedDesc;
    };

    const LOCALE = 'en-GB';
    const baseUrl = process.env.REACT_APP_API_URL || '/api';
    const newsForCard = latestNews.slice(0, 1);

    const formatDate = (dateString, includeYear) => {
        const options = includeYear 
        ? { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'Etc/GMT' }
        : { month: 'long', day: 'numeric', timeZone: 'Etc/GMT' };
        return new Date(dateString).toLocaleDateString(LOCALE, options);
    };

    // Decide what submission action to show based on conference status
    const renderSubmissionAction = (conf) => {
        if (!conf) return null;
        const status = (conf.status || '').toLowerCase();
        if (status === 'open') {
            return (
                <Link to="/dashboard" className="btn btn-primary btn-lg">
                    <i className="bi bi-file-earmark-text me-2"></i>
                    Submit Manuscript
                </Link>
            );
        }
        if (status === 'active') {
            return (
                <button className="btn btn-secondary btn-lg" disabled>
                    <i className="bi bi-hourglass-split me-2"></i>
                    Submissions Will Open Soon
                </button>
            );
        }
        // Closed or Ended or any other status
        return (
            <button className="btn btn-secondary btn-lg" disabled>
                <i className="bi bi-lock me-2"></i>
                Submissions Closed
            </button>
        );
    };

        const getFileIcon = (filename, mimetype) => {
        // Use mimetype first, then fallback to filename extension
        if (mimetype) {
            if (mimetype.includes('pdf')) return 'bi-file-earmark-pdf text-danger';
            if (mimetype.includes('word') || mimetype.includes('document')) return 'bi-file-earmark-word text-primary';
            if (mimetype.includes('excel') || mimetype.includes('spreadsheet')) return 'bi-file-earmark-excel text-success';
            if (mimetype.includes('powerpoint') || mimetype.includes('presentation')) return 'bi-file-earmark-ppt text-warning';
            if (mimetype.includes('image')) return 'bi-file-earmark-image text-info';
        }
        
        // Fallback to filename extension
        const extension = filename?.split('.').pop()?.toLowerCase();
        switch (extension) {
            case 'pdf':
                return 'bi-file-earmark-pdf text-danger';
            case 'doc':
            case 'docx':
                return 'bi-file-earmark-word text-primary';
            case 'xls':
            case 'xlsx':
                return 'bi-file-earmark-excel text-success';
            case 'ppt':
            case 'pptx':
                return 'bi-file-earmark-ppt text-warning';
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif':
            case 'webp':
                return 'bi-file-earmark-image text-info';
            case 'txt':
                return 'bi-file-earmark-text text-secondary';
            default:
                return 'bi-file-earmark text-muted';
        }
    };

    return (
        <div>
            <main className="py-3" style={{ paddingTop: '160px !important' }}>
                <div className="container">
                    {/* Hero Section */}
                    <div className="row mb-3">
                        <div className="col-12 text-center">
                            <div className="hero-section py-3" style={{
                                background: 'linear-gradient(135deg, #ff6b35 0%, #4a90e2 50%, #8e44ad 100%)',
                                borderRadius: '15px',
                                color: 'white',
                                padding: '12px 16px'
                            }}>
                                <h1 className="h3 fw-bold mb-2">Welcome to MFS</h1>
                                <p className="small mb-0">Advancing Global Finance Research and Education</p>
                                {/* <Link to="/dashboard" className="btn btn-light btn-lg px-4 py-2">
                                    <i className="bi bi-file-earmark-text me-2"></i>
                                    Submit Your Paper
                                </Link> */}
                            </div>
                        </div>
                    </div>

                    {/* Main Grid Layout */}
                    <div className="row g-4">
                        {/* Upcoming Conference - Full Width */}
                        <div className="col-12">
                            <div className="card shadow-sm">
                                <div className="card-header" style={{ 
                                    background: 'linear-gradient(135deg, #8e44ad 0%, #4a90e2 50%, #ff6b35 100%)',
                                    backgroundColor: 'transparent', 
                                    color: 'white' }}>
                                    <h4 className="card-title mb-0 text-white">
                                        <i className="bi bi-calendar-event me-2"></i>
                                        Upcoming Conference
                                    </h4>
                                </div>
                                <div className="card-body">
                                    {loading ? (
                                        <div className="text-center">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </div>
                                    ) : latestConference ? (
                                        <div className="row align-items-stretch" style={{ minHeight: '100%' }}>
                                            <div className="col-lg-8">
                                                <h2 className="card-title text-primary">
                                                    <Link to={`/conference/${latestConference._id}`} className="text-decoration-none text-primary">
                                                        {latestConference.title}
                                                    </Link>
                                                </h2>
                                                <h3 className="card-text">
                                                    <i className="bi bi-geo-alt me-1"></i>
                                                    {latestConference.location}
                                                </h3>
                                                <h5 className="card-text">
                                                    <i className="bi bi-calendar3 me-1"></i>
                                                    {formatDate(latestConference.startDate, false)} - {formatDate(latestConference.endDate, true)}
                                                </h5>
                                                <div className="card-text mb-3" style={{maxWidth: '100%', overflow: 'hidden'}}>
                                                    {latestConference.description && (
                                                        <div 
                                                            style={{
                                                                maxWidth: '100%'
                                                            }} 
                                                            className="conference-description"
                                                        >
                                                            <style>
                                                                {`
                                                                .conference-description img {
                                                                    max-width: 100% !important;
                                                                    height: auto !important;
                                                                    width: auto !important;
                                                                }
                                                                `}
                                                            </style>
                                                            <HTMLPreview 
                                                                description={latestConference.description}
                                                                files={latestConference?.files || []}
                                                                endpoint="conferences"
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="col-lg-4 text-center d-flex flex-column h-100 align-items-center justify-content-end align-self-end">

                                                <div className="d-flex flex-column gap-3 mt-auto w-100">
                                                    <Link to={`/conference/${latestConference._id}`} className="btn btn-primary btn-lg">
                                                        <i className="bi bi-info-circle me-2"></i>
                                                        Conference Information
                                                    </Link>

                                                    {renderSubmissionAction(latestConference)}

                                                    <Link to="/dashboard?tab=submissions" className="btn btn-primary btn-lg">
                                                        <i className="bi bi-journal-check me-2"></i>
                                                        Registration
                                                    </Link>


                                                    <Link to={`/conference/${latestConference._id}#accommodation`} className="btn btn-primary btn-lg">
                                                        <i className="bi bi-building me-2"></i>
                                                        Hotel Reservation
                                                    </Link>


                                                {(() => {
                                                    const bookletFile = latestConference?.files?.find(f => f.isBooklet);
                                                    if (!bookletFile) return null;
                                                    return (
                                                        <a
                                                            href={`${baseUrl}/conferences/files/${bookletFile.filename}`}
                                                            download={bookletFile.originalName || bookletFile.filename}
                                                            className="btn btn-primary btn-lg"
                                                            title="Download conference program"
                                                        >
                                                            <i className="bi bi-journal-arrow-down me-2"></i>
                                                            Conference Program
                                                        </a>
                                                    );
                                                })()}


                                          
                                                    <Link to="/conferences" className="btn btn-primary btn-lg">
                                                        <i className="bi bi-archive me-2"></i>
                                                        Past Conferences
                                                    </Link>          

                                                    <a
                                                        href="mailto:mfs@mfsociety.org"
                                                        className="btn btn-primary btn-lg"
                                                        title="Email conference organizers"
                                                    >
                                                        <i className="bi bi-envelope-paper me-2"></i>
                                                        Conference Inquiry
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center text-muted py-5">
                                            <i className="bi bi-calendar-x display-6 mb-3"></i>
                                            <h5>No upcoming conferences</h5>
                                            <p>Check back later for conference announcements</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>


                        {/* News Section */}
                        <div className="col-12">
                            <div className="card shadow-sm">
                                <div className="card-header" style={{ backgroundColor: '#4a90e2', color: 'white' }}>
                                    <h4 className="card-title mb-0 text-white">
                                        <i className="bi bi-newspaper me-2"></i>
                                        Latest News
                                    </h4>
                                </div>
                                <div className="card-body">
                                    {loading ? (
                                        <div className="text-center">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </div>
                                    ) : latestNews.length > 0 ? (
                                        <div className="row g-3">
                                            {latestNews.map((news, index) => (
                                                <div key={news._id || index} className="col-md-6">
                                                    <div className="news-item border-start border-4 border-primary ps-3 mb-3">
                                                        <h6 className="fw-bold text-primary">{news.title}</h6>
                                                        <p className="text-muted small mb-1">
                                                            <i className="bi bi-calendar3 me-1"></i>
                                                            {new Date(news.updatedAt || news.createdAt).toLocaleDateString(LOCALE, { timeZone: 'Etc/GMT' })}
                                                        </p>
                                                        <div className="small mb-2">
                                                            <HTMLPreview 
                                                                description={news.description}
                                                                files={news?.files || []}
                                                                endpoint="news"
                                                            />
                                                        </div>
                                                        <Link to={`/news/${news._id}`} className="btn btn-outline-primary btn-sm">
                                                            Read More
                                                        </Link>                         
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center text-muted">
                                            <i className="bi bi-newspaper display-6 mb-3"></i>
                                            <p>No news available</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>


                        {/* Multinational Finance Journal */}
                        <div className="col-12">
                            <div className="card shadow-sm">
                                <div className="card-header" style={{ backgroundColor: '#2c3e50', color: 'white' }}>
                                    <h4 className="card-title mb-0 text-white">
                                        <i className="bi bi-journal-text me-2"></i>
                                        Multinational Finance Journal
                                    </h4>
                                </div>
                                <div className="card-body">
                                    <div className="row align-items-center">
                                        <div className="col-lg-8">
                                            <p className="card-text">
                                                The Multinational Finance Journal is our flagship publication, featuring 
                                                peer-reviewed research on international finance, cross-border investments, 
                                                currency markets, and global financial markets.
                                            </p>
                                            <p className="card-text">
                                                Published quarterly, the journal serves as a platform for groundbreaking 
                                                research that shapes the understanding of multinational finance. Our rigorous 
                                                peer-review process ensures the highest quality of academic content.
                                            </p>
                                            <div className="d-flex gap-2">
                                                <a
                                                    href="https://sites.uwasa.fi/mfjuva/"
                                                    className="btn btn-primary"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <i className="bi bi-journal me-2"></i>
                                                    Journal Home
                                                </a>
                                            </div>
                                        </div>
                                        <div className="col-lg-4 text-center">
                                            <div className="journal-stats bg-light p-3 rounded">
                                                <h5 className="text-primary mb-3">Journal Metrics</h5>
                                                <div className="row text-center">
                                                    <div className="col-4">
                                                        <h6 className="fw-bold text-primary">4.2</h6>
                                                        <small className="text-muted">Impact Factor</small>
                                                    </div>
                                                    <div className="col-4">
                                                        <h6 className="fw-bold text-success">28</h6>
                                                        <small className="text-muted">Years Publishing</small>
                                                    </div>
                                                    <div className="col-4">
                                                        <h6 className="fw-bold text-info">500+</h6>
                                                        <small className="text-muted">Articles Published</small>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>


                        {/* Welcome Note from Board of Directors */}
                        <div className="col-lg-8">
                            <div className="card h-100 shadow-sm">
                                <div className="card-header bg-primary text-white">
                                    <h3 className="card-title mb-0 text-white">
                                        <i className="bi bi-person-lines-fill me-2"></i>
                                        Welcome Note from the Board of Directors
                                    </h3>
                                </div>
                                <div className="card-body">
                                    <p className="card-text">
                                        Dear Colleagues and Researchers,
                                    </p>
                                    <p className="card-text">
                                        Welcome to the Multinational Finance Society! Since our founding in 1995, we have been dedicated to 
                                        advancing the understanding of international finance and fostering collaboration among researchers, 
                                        practitioners, and academics worldwide.
                                    </p>
                                    <p className="card-text">
                                        Our mission is to promote excellence in multinational finance research, facilitate the exchange of 
                                        innovative ideas, and provide a platform for emerging scholars to showcase their work. Through our 
                                        annual conferences, publications, and networking events, we continue to shape the future of global finance.
                                    </p>
                                    <p className="card-text">
                                        We invite you to join our community of distinguished researchers and contribute to the advancement 
                                        of multinational finance knowledge. Together, we can address the complex challenges facing the 
                                        global financial landscape.
                                    </p>
                                    <div className="text-end">
                                        <strong>- The Board of Directors</strong>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* About MFS */}
                        <div className="col-lg-4">
                            <div className="card shadow-sm">
                                <div className="card-header" style={{ backgroundColor: '#8e44ad', color: 'white' }}>
                                    <h4 className="card-title mb-0 text-white">
                                        <i className="bi bi-building me-2"></i>
                                        About MFS
                                    </h4>
                                </div>
                                <div className="card-body">
                                    <p className="card-text">
                                        The Multinational Finance Society is a premier academic organization dedicated to 
                                        advancing research in international finance and fostering collaboration among 
                                        global finance professionals.
                                    </p>
                                    <p className="card-text">
                                        Founded in 1995, we bring together researchers, practitioners, and students to 
                                        explore cutting-edge topics in multinational finance.
                                    </p>
                                    <Link to="/about" className="btn btn-outline-secondary btn-sm">
                                        Learn More About Us
                                    </Link>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </main>
        </div>
    );
};

export default Home;